import { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/joy';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useSearchParams } from 'react-router';
import ChatsPane from '../components/MessagesChat/ChatsPane';
import MessagesPane from '../components/MessagesChat/MessagesPane';
import { APIGetChats, APISearchAllChats, APISearchByTags, APISearchChats } from '../api';
import { LayoutContext } from '../context';
import '../components/MessagesChat/messages.css';

const chatKey = (chat) => String(chat?.id ?? chat?.chat_id ?? '');
const unpackChats = (data) => Array.isArray(data) ? data : data?.chats || [
  ...(data?.communities || []), ...(data?.regular_chats || []),
];

function matchesFilters(chat, filters) {
  if (filters.type === 'community' && !chat.is_community) return false;
  if (filters.type === 'personal' && chat.is_community) return false;
  if (filters.city && !String(chat.city || '').toLowerCase().includes(filters.city.toLowerCase())) return false;
  const count = Number(chat.member_count || 0);
  if (filters.memberCount === 'small' && count > 10) return false;
  if (filters.memberCount === 'medium' && (count < 10 || count > 50)) return false;
  if (filters.memberCount === 'large' && count < 50) return false;
  if (filters.tags?.length) {
    const tags = Array.isArray(chat.tags) ? chat.tags : String(chat.tags || '').split(',');
    if (!filters.tags.some(tag => tags.some(value => value.trim().toLowerCase() === tag.toLowerCase()))) return false;
  }
  return true;
}

/** В контейнере 1000 px со сайдбаром два столбца сжимают переписку.
 * Список и разговор занимают всю доступную ширину и сменяют друг друга. */
export default function MessagesPage() {
  const { token, chats = [], setChats } = useContext(LayoutContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const chatId = searchParams.get('chatId');
  const [selectedChat, setSelectedChat] = useState(null);
  const [activeFilters, setActiveFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const searchSequence = useRef(0);
  const reduceMotion = useReducedMotion();
  const filtering = Object.entries(activeFilters).some(([key, value]) =>
    key === 'type' ? value && value !== 'all' : Array.isArray(value) ? value.length : Boolean(value));
  const visibleChats = searchResults ?? chats;

  useEffect(() => {
    let alive = true;
    if (!token) return undefined;
    setLoading(true);
    setError('');
    APIGetChats(token).then(data => {
      if (alive) setChats(data?.chats || []);
    }).catch(() => {
      if (alive) setError('Не удалось загрузить чаты. Попробуйте ещё раз.');
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reload]);

  useEffect(() => {
    const requestId = ++searchSequence.current;
    const query = searchQuery.trim();
    if (!token || (!query && !filtering)) {
      setSearchResults(null);
      setSearchLoading(false);
      return undefined;
    }
    setSearchLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const data = query
          ? query.startsWith('#') && query.slice(1).trim()
            ? await APISearchByTags(token, query.slice(1).trim())
            : await APISearchAllChats(token, query)
          : await APISearchChats(token, activeFilters);
        if (requestId !== searchSequence.current) return;
        setSearchResults(unpackChats(data).filter(chat => matchesFilters(chat, activeFilters)));
      } catch (_) {
        if (requestId !== searchSequence.current) return;
        const needle = query.replace(/^#/, '').toLowerCase();
        setSearchResults(chats.filter(chat => matchesFilters(chat, activeFilters) && (!needle || [
          chat.display_name, chat.name, chat.last_message?.content, chat.tags,
          ...(chat.participants || []).map(participant => participant.name),
        ].some(value => String(value || '').toLowerCase().includes(needle)))));
      } finally {
        if (requestId === searchSequence.current) setSearchLoading(false);
      }
    }, query ? 250 : 0);
    return () => { window.clearTimeout(timer); searchSequence.current += 1; };
  }, [token, searchQuery, activeFilters, chats, filtering]);

  useEffect(() => {
    if (!chatId) { setSelectedChat(null); return; }
    const selected = [...chats, ...(searchResults || [])].find(chat => chatKey(chat) === String(chatId));
    if (selected) setSelectedChat(previous => chatKey(previous) === chatKey(selected) ? { ...previous, ...selected } : selected);
    else setSelectedChat(previous => chatKey(previous) === String(chatId) ? previous : null);
  }, [chatId, chats, searchResults]);

  const selectChat = (chat) => {
    setSelectedChat(chat);
    const params = new URLSearchParams(searchParams);
    params.set('chatId', chatKey(chat));
    setSearchParams(params);
  };

  const returnToList = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('chatId');
    setSearchParams(params);
    setSelectedChat(null);
  };

  useEffect(() => {
    const onEscape = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || !chatId) return;
      if (document.querySelector('[role="dialog"]')) return;
      if (event.target.closest?.('input, textarea, [contenteditable="true"]')) return;
      returnToList();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [chatId, searchParams]);

  return (
    <Box className="lv-messages" sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <AnimatePresence initial={false} mode="wait">
        <Box
          component={motion.section}
          key={chatId ? `chat-${chatId}` : 'chat-list'}
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduceMotion ? undefined : { opacity: 0, y: -5 }}
          transition={{ duration: reduceMotion ? 0 : 0.16 }}
          sx={{ flex: 1, minHeight: 0, height: '100%', minWidth: 0 }}
        >
          {chatId ? selectedChat ? (
            <MessagesPane chat={selectedChat} onBack={returnToList} setSelectedChat={setSelectedChat}
              searchParams={searchParams} setSearchParams={setSearchParams} chats={chats} setChats={setChats} />
          ) : (
            <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', p: 3 }}>
              {loading ? <CircularProgress aria-label="Загрузка чата" /> : (
                <Box sx={{ textAlign: 'center', maxWidth: 360 }}>
                  <Typography level="h4" sx={{ mb: 1 }}>Чат недоступен</Typography>
                  <Typography level="body-sm" sx={{ mb: 2 }}>Вернитесь к списку и выберите другой разговор.</Typography>
                  <Button variant="soft" onClick={returnToList}>К списку чатов</Button>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
              {error && <Alert color="warning" sx={{ m: 2, mb: 0 }} endDecorator={<Button size="sm" variant="soft" onClick={() => setReload(value => value + 1)}>Повторить</Button>}>{error}</Alert>}
              <ChatsPane chats={visibleChats} setChats={setChats} selectedChatId={null}
                handleChatSelect={selectChat} onApplyFilters={setActiveFilters}
                onSearch={setSearchQuery} searchQuery={searchQuery} activeFilters={activeFilters}
                loading={loading || searchLoading} isFiltered={Boolean(searchQuery.trim() || filtering)} />
            </Box>
          )}
        </Box>
      </AnimatePresence>
    </Box>
  );
}
